import {gql} from '@apollo/client';
import {createStyles, Group, Paper, Skeleton} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';
import _ from 'underscore';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import Spinner from 'ui-library/Spinner';

import {HomepageV2AssignedProcessTasksQuery} from './__generated/HomepageV2AssignedProcessTasksQuery';
import CardSection, {CardSectionLoadingRows} from './CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT, LOADING_SKELETON_ROW_COUNT} from './HomepageConstants';
import {
  getEnabledMyWorkCategories,
  hasReviewsLicense,
  shouldShowAssignedProcessTasks,
  shouldShowTemplateReviews,
} from './HomepageVisibility';
import MyWorkSectionContent from './my-work-v2/MyWorkSectionContent';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

const useMyWorkSectionLoadingStyles = createStyles((theme) => ({
  filterRow: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: theme.spacing.md,
  },
  categoryLoadingSpinner: {
    '&&': {
      width: '16px',
      height: '16px',
    },
  },
}));

export const HOMEPAGE_V2_ASSIGNED_PROCESS_TASKS_QUERY = gql`
  query HomepageV2AssignedProcessTasksQuery @skipAudit @engComponent(name: NAVIGATION) {
    assignedProcessTaskStatusSummaries(
      processTaskStatusTypes: [PENDING, IN_PROGRESS, COMPLETED, FAILED, INVALID, SKIPPED, CANCELLED]
    ) {
      ... on ProcessTaskStatusSummaryItemNode {
        count
        taskIds
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
            statusType
          }
        }
      }
    }
  }
`;

/** Entry, worksheet, template, and process-task work queues with per-category status filters. */
export default function MyWorkSection(): JSX.Element | null {
  const showAssignedProcessTasks = shouldShowAssignedProcessTasks();

  useOnMount(() => {
    // Browsers cap the number of simultaneous network requests to a given domain - typically around 6.
    // <Sidepanel> (which renders this component) does a few expensive GraphQL queries, and since Apollo
    // always defers queries for a tick, if we don't match that behavior here, these network requests are
    // fired first, and ultimately make page load slower (we want the slow network requests to start as soon
    // as possible).
    _.defer(() => {
      if (hasReviewsLicense()) {
        HomeDashboardActions.fetchEntryReviewSummary();
        HomeDashboardActions.fetchWorksheetReviewSummary();
      }
      if (shouldShowTemplateReviews()) {
        HomeDashboardActions.fetchTemplateReviewSummary();
      }
    });
  });

  const entryReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getEntryReviewSummary(), [])
  );
  const worksheetReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getWorksheetReviewSummary(), [])
  );
  const templateReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getTemplateReviewSummary(), [])
  );
  const entryReviewSummaryFetchFailed = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getEntryReviewSummaryFetchFailed(), [])
  );
  const worksheetReviewSummaryFetchFailed = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getWorksheetReviewSummaryFetchFailed(), [])
  );
  const templateReviewSummaryFetchFailed = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getTemplateReviewSummaryFetchFailed(), [])
  );

  const enabledCategories = useMemo(
    () => getEnabledMyWorkCategories({entryReviewSummary, worksheetReviewSummary, templateReviewSummary}),
    [entryReviewSummary, worksheetReviewSummary, templateReviewSummary]
  );

  const tasksQueryResult = useSkippableBenchlingQuery<HomepageV2AssignedProcessTasksQuery>(
    HOMEPAGE_V2_ASSIGNED_PROCESS_TASKS_QUERY,
    {skip: !showAssignedProcessTasks}
  );

  const isEntryReviewSummaryPending = entryReviewSummary == null && !entryReviewSummaryFetchFailed;
  const isWorksheetReviewSummaryPending =
    worksheetReviewSummary == null && !worksheetReviewSummaryFetchFailed;
  const isTemplateReviewSummaryPending = templateReviewSummary == null && !templateReviewSummaryFetchFailed;
  const isWaitingOnReviewSummaries =
    (hasReviewsLicense() && (isEntryReviewSummaryPending || isWorksheetReviewSummaryPending)) ||
    (shouldShowTemplateReviews() && isTemplateReviewSummaryPending);
  const isWaitingOnTasksSummary =
    showAssignedProcessTasks && tasksQueryResult.type === QueryResultType.PENDING;
  const isCategoryDataLoading = isWaitingOnReviewSummaries || isWaitingOnTasksSummary;
  const {classes: loadingClasses} = useMyWorkSectionLoadingStyles();

  if (isCategoryDataLoading) {
    return (
      <CardSection title="My work" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
        <Group spacing="xs" className={loadingClasses.filterRow}>
          <Spinner classNames={loadingClasses.categoryLoadingSpinner} light={true} />
        </Group>
        <div>
          <CardSectionLoadingRows count={LOADING_SKELETON_ROW_COUNT} />
        </div>
      </CardSection>
    );
  }

  if (enabledCategories.length === 0) {
    return null;
  }

  return (
    <MyWorkSectionContent
      entryReviewSummary={entryReviewSummary}
      worksheetReviewSummary={worksheetReviewSummary}
      templateReviewSummary={templateReviewSummary}
      tasksQueryResult={tasksQueryResult}
      enabledCategories={enabledCategories}
    />
  );
}

const PLACEHOLDER_FILTER_COUNT = 5;

export function MyWorkSectionPlaceholder({proxyCardClassName}: {proxyCardClassName: string}): JSX.Element {
  return (
    <Paper radius="lg" withBorder={true} className={proxyCardClassName}>
      <Group position="apart" mb="md">
        {_.range(PLACEHOLDER_FILTER_COUNT).map((index) => (
          <Skeleton
            height={10}
            width={`calc((100% / ${PLACEHOLDER_FILTER_COUNT}) - 1rem)`}
            animate={false}
            key={index}
          />
        ))}
      </Group>
      <MyWorkSectionPlaceholderSkeleton mb="md" />
      <MyWorkSectionPlaceholderSkeleton />
    </Paper>
  );
}

function MyWorkSectionPlaceholderSkeleton({mb}: {mb?: string}): JSX.Element {
  const width = useMemo(() => Math.random() * 60 + 20, []);
  return (
    <Group position="apart" mb={mb}>
      <Skeleton height={10} width={`${width}%`} animate={false} />
      <Skeleton height={10} width="15%" animate={false} />
    </Group>
  );
}
